import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Typography } from '@mui/material';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DashboardPreferencesProvider } from './context/DashboardPreferencesContext';
import ErrorBoundary from './components/ErrorBoundary';

// Import Pages
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import DashboardPage from './components/DashboardPage';
import AdminPage from './components/AdminPage';
import ManageUsersPage from './components/ManageUsersPage';
import ManageSmartDevicesPage from './components/ManageSmartDevicesPage';
import GraphPage from './components/GraphPage';
import MonthlyAveragesPage from './components/MonthlyAveragesPage';
import PowerCostPage from './components/PowerCostPage';
import NotFoundPage from './components/NotFoundPage';
import EmailVerificationPage from './components/EmailVerificationPage';
import GenericErrorPage from './components/GenericErrorPage';

// A private route component to protect routes
const PrivateRoute = ({ children, adminOnly = false }) => {
  const { t } = useTranslation();
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>{t('common.loadingAuthentication')}</Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && (!user || user.role !== 'ROLE_ADMIN')) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  const { i18n } = useTranslation();

  // You can set initial language here if needed, or rely on browser detection
  useEffect(() => {
    // Example: set language based on a user preference or URL param
    // i18n.changeLanguage('en');
  }, [i18n]);

  return (
      <Router>
        <AuthProvider>
          <DashboardPreferencesProvider>
          <ErrorBoundary>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/verify-email" element={<EmailVerificationPage />} />
            <Route path="/error" element={<GenericErrorPage />} />
            <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <DashboardPage />
                  </PrivateRoute>
                }
            />
            <Route
                path="/admin"
                element={
                  <PrivateRoute adminOnly={true}>
                    <AdminPage />
                  </PrivateRoute>
                }
            />
            <Route
                path="/admin/manage-users"
                element={
                  <PrivateRoute adminOnly={true}>
                    <ManageUsersPage />
                  </PrivateRoute>
                }
            />
            <Route
                path="/admin/manage-smart-devices"
                element={
                  <PrivateRoute adminOnly={true}>
                    <ManageSmartDevicesPage />
                  </PrivateRoute>
                }
            />
            <Route
                path="/graph/:zone?"
                element={
                  <PrivateRoute>
                    <GraphPage />
                  </PrivateRoute>
                }
            />
            <Route
                path="/monthly-averages"
                element={
                  <PrivateRoute>
                    <MonthlyAveragesPage />
                  </PrivateRoute>
                }
            />
            <Route
                path="/power-cost"
                element={
                  <PrivateRoute>
                    <PowerCostPage />
                  </PrivateRoute>
                }
            />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </ErrorBoundary>
          </DashboardPreferencesProvider>
        </AuthProvider>
      </Router>
  );
}

export default App;